import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import ProductCard, { Product } from '../components/ProductCard';
import Header from '../components/Header';
import Footer from '../components/Footer';

interface CategoryInfo {
  id: string;
  name: string;
  icon: string;
  count: number;
  subcategories: string[];
}

const CategoryPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [categoryInfo, setCategoryInfo] = useState<CategoryInfo | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const LIMIT = 24;

  // 카테고리 정보 로드
  useEffect(() => {
    const ctrl = new AbortController();
    fetch('/api/storefront/categories', { signal: ctrl.signal })
      .then(r => r.json())
      .then(d => {
        const cat = d.categories?.find((c: CategoryInfo) => c.id === categoryId);
        setCategoryInfo(cat || null);
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, [categoryId]);

  // 카테고리 변경 시 상품 초기 로드
  useEffect(() => {
    if (!categoryId) return;
    // 이전 요청 취소
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setProducts([]);
    setTotal(0);
    setLoading(true);

    fetch(`/api/storefront/list?main_category=${categoryId}&limit=${LIMIT}&offset=0&published=1`, { signal: ctrl.signal })
      .then(r => r.json())
      .then(data => {
        if (!ctrl.signal.aborted) {
          setProducts(data.products || []);
          setTotal(data.total || 0);
        }
      })
      .catch(() => {})
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });

    return () => ctrl.abort();
  }, [categoryId]);

  // 페이지 타이틀 업데이트
  useEffect(() => {
    if (categoryInfo) {
      document.title = `${categoryInfo.name} - WorldShip`;
    }
    return () => { document.title = 'WorldShip - 해외직구 쇼핑몰'; };
  }, [categoryInfo]);

  const handleLoadMore = async () => {
    if (!categoryId || loadingMore) return;
    setLoadingMore(true);
    try {
      const resp = await fetch(`/api/storefront/list?main_category=${categoryId}&limit=${LIMIT}&offset=${products.length}&published=1`);
      const data = await resp.json();
      setProducts(prev => [...prev, ...(data.products || [])]);
      setTotal(data.total || 0);
    } catch {
      // ignore
    } finally {
      setLoadingMore(false);
    }
  };

  const hasMore = products.length < total;

  return (
    <>
    <Header />
    <div className="category-page">
      {/* 브레드크럼 */}
      <div className="category-page-breadcrumb">
        <Link to="/">홈</Link>
        <span>›</span>
        <span>{categoryInfo?.name || categoryId}</span>
      </div>

      {/* 헤더 */}
      <div className="category-page-header">
        <h1 className="category-page-title">
          {categoryInfo?.name || categoryId}
        </h1>
        {total > 0 && (
          <p className="category-page-count">{total.toLocaleString()}개 상품</p>
        )}
      </div>

      {/* 상품 그리드 */}
      {loading ? (
        <div className="category-empty">상품을 불러오는 중...</div>
      ) : products.length === 0 ? (
        <div className="category-empty">이 카테고리에 상품이 없습니다.</div>
      ) : (
        <>
          <div className="category-product-grid">
            {products.map(p => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>

          {hasMore && (
            <div className="category-load-more">
              <button onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? '불러오는 중...' : `더보기 (${products.length}/${total})`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
    <Footer />
    </>
  );
};

export default CategoryPage;
